import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Scroll, MagnifyingGlass, Printer, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import PrintLabelsDialog from "@/components/PrintLabelsDialog";
import { fetchAndPrintLabels } from "@/lib/printLabels";
import BulkInventoryDialog from "@/components/BulkInventoryDialog";
import InventoryReplaceDialog from "@/components/InventoryReplaceDialog";
import ProductCombobox from "@/components/ProductCombobox";
import { ROLL_EXPORT_COLUMNS, downloadCsv } from "@/lib/incoming";

const empty = {
  product_id: "", batch_number: "", manufacturing_date: new Date().toISOString().slice(0, 10),
  initial_length_m: 22.86, current_length_m: "", location: "", width_m: 1.52,
};

export default function RollsPage() {
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(new Set());
  const [printOpen, setPrintOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const params = {};
    if (productFilter !== "all") params.product_id = productFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    else params.archived = false;
    if (search) params.search = search;
    const [{ data: r }, { data: p }, { data: m }, { data: s }] = await Promise.all([
      api.get("/rolls", { params }), api.get("/products"), api.get("/manufacturers"), api.get("/series"),
    ]);
    setRolls(r); setProducts(p); setMfrs(m); setSeries(s);
  };
  useEffect(() => { load(); }, [statusFilter, productFilter, search]);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map((s) => [s.id, s]));

  const openCreate = () => { setEditing(null); setForm({ ...empty, product_id: products[0]?.id || "" }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      product_id: r.product_id, batch_number: r.batch_number, manufacturing_date: r.manufacturing_date,
      initial_length_m: r.initial_length_m, current_length_m: r.current_length_m,
      location: r.location || "", width_m: r.width_m,
    });
    setOpen(true);
  };

  const onSave = async () => {
    try {
      const payload = {
        ...form,
        initial_length_m: Number(form.initial_length_m),
        current_length_m: form.current_length_m === "" ? null : Number(form.current_length_m),
        width_m: Number(form.width_m),
      };
      if (editing) {
        await api.put(`/rolls/${editing.id}`, payload);
        toast.success("Roll updated");
      } else {
        const { data: created } = await api.post("/rolls", payload);
        toast.success("Roll added — printing label…");
        fetchAndPrintLabels([created.id]).catch(() => toast.error("Roll saved, but the label couldn't be printed automatically."));
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onDelete = async (r) => {
    try { await api.delete(`/rolls/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onExport = () => {
    if (rolls.length === 0) { toast.error("No rolls to export"); return; }
    const data = rolls.map((r) => {
      const p = productMap[r.product_id];
      return [
        r.sku, r.batch_number,
        mfrMap[p?.manufacturer_id]?.name || "",
        seriesMap[p?.series_id]?.name || "",
        p?.name || "",
        r.current_length_m, r.initial_length_m, r.width_m,
        r.location || "", r.manufacturing_date || "", r.expiry_date || "", r.status,
      ];
    });
    downloadCsv(`rolls-export-${new Date().toISOString().slice(0, 10)}.csv`, ROLL_EXPORT_COLUMNS, data);
    toast.success(`Exported ${rolls.length} roll(s)`);
  };

  return (
    <div className="p-8 max-w-[1500px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Stock</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Rolls</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onExport} className="gap-2 rounded-md" data-testid="export-rolls-btn">
            <DownloadSimple size={16} /> Export CSV
          </Button>
          <InventoryReplaceDialog onSuccess={load} />
          <BulkInventoryDialog onSuccess={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} disabled={products.length === 0} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-roll-btn">
                <Plus size={16} /> Add Roll
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Roll</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Product</Label>
                <ProductCombobox
                  products={products.filter((p) => p.inventory_type !== "count")}
                  mfrMap={mfrMap}
                  seriesMap={seriesMap}
                  value={form.product_id}
                  onChange={(v) => setForm({ ...form, product_id: v })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Batch number</Label>
                <Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="B-240115-A" className="font-mono" data-testid="roll-batch-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Manufacturing date</Label>
                <Input type="date" value={form.manufacturing_date} onChange={(e) => setForm({ ...form, manufacturing_date: e.target.value })} className="font-mono" data-testid="roll-mfg-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Initial length (m)</Label>
                <Input type="number" step="0.01" value={form.initial_length_m} onChange={(e) => setForm({ ...form, initial_length_m: e.target.value })} className="font-mono" data-testid="roll-initial-len" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Current length (m)</Label>
                <Input type="number" step="0.01" value={form.current_length_m} placeholder={editing ? "" : "= initial"} onChange={(e) => setForm({ ...form, current_length_m: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Width (m)</Label>
                <Input type="number" step="0.01" value={form.width_m} onChange={(e) => setForm({ ...form, width_m: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Shelf A3" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={onSave} disabled={!form.product_id || !form.batch_number || !form.manufacturing_date}
                className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="roll-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product, brand, series, colour, SKU or batch…" className="pl-9 h-10 rounded-md bg-white" data-testid="roll-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="incoming">Incoming</SelectItem>
          </SelectContent>
        </Select>
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="w-60"><SelectValue placeholder="Product" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected.size > 0 && (
          <Button onClick={() => setPrintOpen(true)} className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-md ml-auto" data-testid="bulk-print-btn">
            <Printer size={16} /> Print {selected.size} label{selected.size === 1 ? "" : "s"}
          </Button>
        )}
      </div>

      {rolls.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Scroll size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">No rolls yet.</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                <th className="px-3 py-3 w-10">
                  <Checkbox
                    checked={rolls.length > 0 && selected.size === rolls.length}
                    onCheckedChange={(v) => setSelected(v ? new Set(rolls.map((r) => r.id)) : new Set())}
                    data-testid="roll-select-all"
                  />
                </th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Batch</th>
                <th className="px-4 py-3">Mfg</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3 text-right">Initial</th>
                <th className="px-4 py-3 text-right">Remaining</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {rolls.map((r) => {
                const p = productMap[r.product_id];
                const isSelected = selected.has(r.id);
                return (
                  <tr key={r.id} onClick={() => navigate(`/rolls/${r.id}`)} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer ${isSelected ? "bg-blue-50/40" : ""}`} data-testid={`roll-row-${r.id}`}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(v) => {
                          const next = new Set(selected);
                          if (v) next.add(r.id); else next.delete(r.id);
                          setSelected(next);
                        }}
                        data-testid={`roll-select-${r.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.sku}</td>
                    <td className="px-4 py-3">
                      {p && <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-slate-200 rounded-sm" style={{ background: p.color_hex || "#e2e8f0" }} />
                        <span className="truncate max-w-[160px]">{p.name}</span>
                      </div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.batch_number}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.manufacturing_date}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.expiry_date}</td>
                    <td className="px-4 py-3 font-mono text-right tabular-nums">{r.initial_length_m.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono text-right tabular-nums font-semibold">{r.current_length_m.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.location || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm ${r.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : r.status === "incoming" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>{r.status}</span>
                      {r.status === "depleted" && (
                        <div className="text-[9px] text-amber-600 mt-1 whitespace-nowrap" title="Fully cut — moves to the Archive tab after midnight" data-testid={`roll-grace-${r.id}`}>→ Archive tonight</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-roll-${r.id}`}><PencilSimple size={14} /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`delete-roll-${r.id}`}><Trash size={14} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete roll {r.sku}?</AlertDialogTitle>
                              <AlertDialogDescription>All cut history for this roll will also be removed.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(r)} className="bg-red-600 hover:bg-red-700 rounded-md">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PrintLabelsDialog open={printOpen} onOpenChange={setPrintOpen} rollIds={Array.from(selected)} />
    </div>
  );
}
